import React, { useState, useEffect, useRef } from 'react';
import Lenis from '@studio-freight/lenis';
import './boxArenaTheme.css';
import OpenMapsLink from "./OpenMapsLink";
import appMockup from './boxarena-assets/app-mockup.png';
import heroImg from './boxarena-assets/box-arena.jpg';
import ground1Img from './boxarena-assets/box-arena-g1.jpg';
import ground2Img from './boxarena-assets/box-arena-g2.jpg';
import logoImg from './boxarena-assets/boxarena-logo.webp';
import ongroundLogoImg from '../assets/branding/onground-powered-by-dark-bg.png';
import OngroundStoreIcon from '../OngroundStoreIcon';
import {
  getThemeContent,
  resolveOngroundAppLink,
  resolveVenueSocialLink,
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
} from '../publicWebsiteContent';
import { isWebsiteSectionVisible } from '../themeDefinitions';
import usePublicContactEnquiry from '../usePublicContactEnquiry';
import PublicContactEnquiryStatus, { PublicContactHoneypot, PublicContactPhoneField } from '../PublicContactEnquiryStatus';

// ── Data ──────────────────────────────────────────────────────────────────────
const NAV_LINKS = ['Grounds', 'About', 'Booking', 'Contact'];

const SPORTS = [
  { id: 1, img: ground1Img, name: 'Box Football', emoji: '⚽', tagline: 'Fast-paced 5v5 & 7v7 on FIFA-grade artificial turf.' },
  { id: 2, img: ground2Img, name: 'Pickleball', emoji: '🏓', tagline: 'Nepal\'s favourite new sport — courts always ready.' },
  { id: 3, img: heroImg, name: 'Futsal', emoji: '🎯', tagline: 'Covered all-weather futsal under floodlights.' },
];

const STATS = [
  { n: 3, suffix: '', label: 'Premium Grounds' },
  { n: 500, suffix: '+', label: 'Players / Month' },
  { n: 7, suffix: ' Days', label: 'Always Open' },
  { n: 500, suffix: '/hr', label: 'Starting Price (NPR)' },
];

const TESTIMONIALS = [
  { name: 'Rajan Shrestha', sport: 'Football', rating: 5, text: 'The best turf in Kathmandu. Floodlights are incredible for night sessions. We play every Friday here.' },
  { name: 'Priya Tamang', sport: 'Pickleball', rating: 5, text: 'Excellent courts and staff. The pickleball courts are always well-maintained. Highly recommend!' },
  { name: 'Arun KC', sport: 'Futsal', rating: 5, text: 'We host our office tournaments at BoxArena every quarter. The team is super helpful and the venue is top-tier.' },
];

const FAQS = [
  { q: 'How do I book a slot?', a: 'You can book via the ONGROUND app or by calling us directly at +977-9700426645. Online booking is available 24/7.' },
  { q: 'What is the cancellation policy?', a: 'Free cancellation up to 4 hours before your slot. Cancellations within 4 hours will incur a 50% charge.' },
  { q: 'Do you provide equipment?', a: 'Yes! We have footballs, pickleball paddles, and futsal balls available for rent at the venue.' },
  { q: 'Is parking available?', a: 'We have free parking available on the premises for all visitors.' },
  { q: 'Do you host corporate events?', a: 'Absolutely. We offer tailored packages for corporate tournaments, team outings, and birthday events.' },
];

const ABOUT_FEATURES = [
  '🏟️ Floodlit Courts',
  '🅿️ Free Parking',
  '🎮 Equipment Rental',
  '🌧️ All-Weather Turf',
  '📅 Online Booking',
  '🏆 Tournaments',
];

const BOOKING_FEATURES = [
  'Corporate Tournaments',
  'Birthday Parties & Events',
  'Long-term Academy Bookings',
  'Sponsorship Opportunities',
];

const getText = (value) => String(value || '').trim();

const getSafeExternalUrl = (value, fallback = '') => {
  const input = getText(value);

  if (!input) {
    return fallback;
  }

  try {
    const parsed = new URL(input);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return fallback;
    }

    return parsed.toString();
  } catch {
    return fallback;
  }
};

const SocialIcon = ({ type }) => {
  if (type === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <path d="M17.5 6.5h.01" />
      </svg>
    );
  }

  if (type === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    );
  }

  if (type === 'youtube') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22 12s0-4-1-6c-.6-1-1.4-1.4-2.5-1.6C16.8 4 12 4 12 4s-4.8 0-6.5.4C4.4 4.6 3.6 5 3 6c-1 2-1 6-1 6s0 4 1 6c.6 1 1.4 1.4 2.5 1.6C7.2 20 12 20 12 20s4.8 0 6.5-.4c1.1-.2 1.9-.6 2.5-1.6 1-2 1-6 1-6Z" />
        <path d="m10 9 5 3-5 3V9Z" />
      </svg>
    );
  }

  if (type === 'tiktok') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.3 7.6L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5Z" />
    </svg>
  );
};

const formatTime = (value) => {
  const input = getText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input;
  }

  const [rawHour, minute] = input.split(':');
  const hour = Number(rawHour);

  if (!Number.isFinite(hour)) {
    return input;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const parseStatValue = (value) => {
  const input = getText(value);
  const match = input.match(/^([\d,.]+)(.*)$/);

  if (!match) {
    return {
      n: 0,
      suffix: '',
      rawValue: input,
    };
  }

  const numericValue = Number(match[1].replace(/,/g, ''));

  return {
    n: Number.isFinite(numericValue) ? numericValue : 0,
    suffix: match[2] || '',
    rawValue: input,
  };
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useScrollAnimation(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('ba-in-view');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled]);
}

function useCountUp(target, isActive, suffix) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isActive) return;
    let start = 0;
    const duration = 1800;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, isActive]);
  return count;
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function StarRating({ rating }) {
  return (
    <div className="ba-stars">
      {[...Array(rating)].map((_, i) => <span key={i} className="ba-star">★</span>)}
    </div>
  );
}

function StatItem({
  n,
  suffix,
  label,
  rawValue = '',
  isActive,
}) {
  const count = useCountUp(n, isActive, suffix);
  const displayedValue = n > 0 ? `${count}${suffix}` : rawValue;

  return (
    <div className="ba-stat-item">
      <p className="ba-stat-num">{displayedValue}</p>
      <p className="ba-stat-label">{label}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`ba-faq-item ${open ? 'open' : ''}`}>
      <button className="ba-faq-trigger" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="ba-faq-icon">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="ba-faq-body">
        <p>{a}</p>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const BoxArenaTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = 'clean',
  primaryColor = '#39FF14',
  accentColor = '#0A0A0A',
  heroImage = '',
  subheadline,
  ctaLabel = 'Book Now',
  openBooking,
  previewMode = false,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const [statsActive, setStatsActive] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const statsRef = useRef(null);
  const lenisRef = useRef(null);

  const arena =
    website?.arena && typeof website.arena === 'object'
      ? website.arena
      : {};

  const legacyCopy =
    arena.copy && typeof arena.copy === 'object'
      ? arena.copy
      : {};

  const boxArena = getThemeContent(website, 'box-arena');

  const boxSocial =
    boxArena.social && typeof boxArena.social === 'object'
      ? boxArena.social
      : {};

  const t = (key, fallback, legacyValue = '') =>
    resolveThemeCopy(
      website,
      'box-arena',
      key,
      fallback,
      legacyValue,
    );

  const m = (key, fallback = '') =>
    resolveThemeMedia(
      website,
      'box-arena',
      key,
      fallback,
    );

  const {
    values: formData,
    handleChange: handleFormChange,
    submit: handleFormSubmit,
    submitting: contactSubmitting,
    feedback: contactFeedback,
  } = usePublicContactEnquiry({
    venue,
    previewMode,
    successMessage: t(
      'formSuccessMessage',
      "Message sent! We'll get back to you shortly.",
    ),
  });

  const bookingEnabled = website?.booking?.enabled !== false;

  const resolvedPrimaryColor =
    getText(primaryColor) || '#39FF14';

  const resolvedAccentColor =
    getText(accentColor) || '#0A0A0A';

  const usesOriginalPrimary =
    resolvedPrimaryColor.toLowerCase() === '#39ff14';

  const usesOriginalAccent =
    resolvedAccentColor.toLowerCase() === '#0a0a0a';

  const boxArenaThemeStyle = {
    '--ba-green': resolvedPrimaryColor,

    '--ba-green-dark': usesOriginalPrimary
      ? '#2ACC0E'
      : `color-mix(
          in srgb,
          ${resolvedPrimaryColor} 82%,
          #000000
        )`,

    '--ba-dark': resolvedAccentColor,

    '--ba-dark-card': usesOriginalAccent
      ? '#141414'
      : `color-mix(
          in srgb,
          ${resolvedAccentColor} 88%,
          #ffffff
        )`,

    '--ba-dark-border': usesOriginalAccent
      ? '#1F1F1F'
      : `color-mix(
          in srgb,
          ${resolvedAccentColor} 78%,
          #ffffff
        )`,

    '--ba-text-dark': usesOriginalAccent
      ? '#1A1A1A'
      : resolvedAccentColor,
  };

  const canBook = bookingEnabled && grounds.length > 0;

  const venueName =
    getText(venue?.name) ||
    t('venueNameFallback', 'BoxArena');

  const usesBoxArenaBrand =
    getText(venueName)
      .replace(/[\s_-]+/g, '')
      .toLowerCase()
      .includes('boxarena');

  const city =
    getText(venue?.location?.city || venue?.city) ||
    t('cityFallback', 'Kathmandu');

  const country =
    getText(venue?.location?.country || venue?.country) ||
    t('countryFallback', 'Nepal');

  const address =
    getText(venue?.location?.address) ||
    t(
      'addressFallback',
      `Tangal-02, ${city}, ${country}`,
    );

  const phone =
    getText(
      venue?.contact?.e164 ||
      venue?.contact?.nationalNumber,
    ) || t('phoneFallback', '+977-9700426645');

  const openingTime =
    formatTime(venue?.policies?.openTime) ||
    t('openingTimeFallback', '6:00 AM');

  const closingTime =
    formatTime(venue?.policies?.closeTime) ||
    t('closingTimeFallback', '11:00 PM');

  const hours = `${openingTime} – ${closingTime}`;

  const gallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery.filter(Boolean)
    : [];

  const resolvedHeroImage = m(
    'heroImageUrl',
    getText(
      heroImage ||
      venue?.media?.bannerImage ||
      gallery[0],
    ) || heroImg,
  );

  const resolvedLogoImage = resolveThemeLogo(
    website,
    'box-arena',
    usesBoxArenaBrand ? logoImg : '',
  );
  const resolvedAppImage = m('appImageUrl', appMockup);

  const fallbackGroundImages = [
    m('groundImageOneUrl', ground1Img),
    m('groundImageTwoUrl', ground2Img),
    m('groundImageThreeUrl', heroImg),
  ];

  const resolvedSubheadline =
    getText(subheadline) ||
    getText(venue?.shortDescription) ||
    t(
      'heroDescriptionFallback',
      "Kathmandu's premier multi-sport arena — floodlit courts, all-weather turf, instant booking. Your game starts here.",
    );

  const resolvedCtaLabel =
    getText(ctaLabel) || 'Book Now';

  const resolvedHeroBadge = t(
    'heroLocationBadge',
    `${city}, ${country} · ${venueName}`,
    legacyCopy.heroBadge,
  );

  const navLinks = [
    { target: 'grounds', sectionKey: 'grounds', label: t('navGrounds', 'Grounds') },
    { target: 'about', sectionKey: 'about', label: t('navAbout', 'About') },
    { target: 'booking', sectionKey: 'booking', label: t('navBooking', 'Booking') },
    { target: 'contact', sectionKey: 'contact', label: t('navContact', 'Contact') },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const configuredSports = resolveThemeArray(
    website,
    'box-arena',
    'sports',
    [],
  );

  const getConfiguredSport = (index) => {
    const item = configuredSports[index];
    return item && typeof item === 'object' ? item : {};
  };

  const resolvedSports = grounds.length
    ? grounds.map((ground, index) => {
      const fallbackSport =
        SPORTS[index % SPORTS.length];
      const configuredSport =
        getConfiguredSport(index);

      return {
        id:
          ground?._id ||
          ground?.id ||
          `ground-${index}`,
        ground,
        img:
          getText(configuredSport.imageUrl) ||
          getText(ground?.mainImageUrl) ||
          gallery[index] ||
          fallbackGroundImages[
            index % fallbackGroundImages.length
          ],
        name:
          getText(configuredSport.name) ||
          getText(
            ground?.name ||
            ground?.sportName ||
            ground?.sport?.name ||
            ground?.type,
          ) ||
          `${t('groundFallbackNamePrefix', 'Ground')} ${index + 1}`,
        emoji:
          getText(configuredSport.emoji) ||
          fallbackSport.emoji,
        tagline:
          getText(configuredSport.tagline) ||
          getText(ground?.type) ||
          fallbackSport.tagline,
      };
    })
    : (configuredSports.length > 0
      ? configuredSports
      : SPORTS
    ).map((sport, index) => {
      const fallbackSport =
        SPORTS[index % SPORTS.length];

      return {
        id: sport?.id || `sport-${index}`,
        ground: null,
        img:
          getText(sport?.imageUrl || sport?.img) ||
          fallbackGroundImages[
            index % fallbackGroundImages.length
          ],
        name:
          getText(sport?.name) ||
          fallbackSport.name,
        emoji:
          getText(sport?.emoji) ||
          fallbackSport.emoji,
        tagline:
          getText(sport?.tagline) ||
          fallbackSport.tagline,
      };
    });

  const rawStats = resolveThemeArray(
    website,
    'box-arena',
    'stats',
    STATS,
    arena.stats,
  );

  const resolvedStats = rawStats
    .map((stat) => {
      if (stat?.n !== undefined) {
        return stat;
      }

      const parsedValue = parseStatValue(stat?.value);

      return {
        ...parsedValue,
        label: getText(stat?.label),
      };
    })
    .filter((stat) =>
      getText(stat?.label) &&
      (Number(stat?.n) > 0 || getText(stat?.rawValue)),
    );

  const rawTestimonials = resolveThemeArray(
    website,
    'box-arena',
    'testimonials',
    TESTIMONIALS,
    arena.testimonials,
  );

  const resolvedTestimonials = rawTestimonials
    .map((testimonial) => ({
      name: getText(testimonial?.name),
      sport: getText(
        testimonial?.sport || testimonial?.role,
      ),
      rating: Math.max(
        0,
        Math.min(
          5,
          Number(testimonial?.rating) || 0,
        ),
      ),
      text: getText(testimonial?.text),
    }))
    .filter((testimonial) =>
      testimonial.name && testimonial.text,
    );

  const rawFaqs = resolveThemeArray(
    website,
    'box-arena',
    'faqs',
    FAQS,
    arena.faqs,
  );

  const resolvedFaqs = rawFaqs
    .map((faq) => ({
      q: getText(faq?.q || faq?.question),
      a: getText(faq?.a || faq?.answer),
    }))
    .filter((faq) => faq.q && faq.a);

  const resolvedAboutFeatures = resolveThemeArray(
    website,
    'box-arena',
    'aboutFeatures',
    ABOUT_FEATURES.map((text) => ({ text })),
  )
    .map((item) => getText(item?.text || item))
    .filter(Boolean);

  const resolvedBookingFeatures = resolveThemeArray(
    website,
    'box-arena',
    'bookingFeatures',
    BOOKING_FEATURES.map((text) => ({ text })),
  )
    .map((item) => getText(item?.text || item))
    .filter(Boolean);

  const configuredInstagramUrl =
    getSafeExternalUrl(boxSocial.instagramUrl);

  const instagramHandle =
    getText(boxSocial.instagramHandle);

  const instagramUrl = resolveVenueSocialLink(
    configuredInstagramUrl,
    'instagram',
  );
  const facebookUrl = resolveVenueSocialLink(
    boxSocial.facebookUrl,
    'facebook',
  );
  const youtubeUrl = getSafeExternalUrl(
    boxSocial.youtubeUrl,
  );
  const tiktokUrl = resolveVenueSocialLink(
    boxSocial.tiktokUrl,
    'tiktok',
  );
  const whatsappUrl = resolveVenueSocialLink(
    boxSocial.whatsappUrl,
    'whatsapp',
  );
  const googlePlayUrl = resolveOngroundAppLink(
    boxSocial.googlePlayUrl,
    'googlePlay',
  );
  const appStoreUrl = resolveOngroundAppLink(
    boxSocial.appStoreUrl,
    'appStore',
  );
  const ongroundUrl = getSafeExternalUrl(
    boxSocial.ongroundUrl,
    'https://getonground.com',
  );
  const resolvedPoweredLogo = m(
    'poweredLogoUrl',
    ongroundLogoImg,
  );

  const footerSocialItems = [
    {
      key: 'instagram',
      label: t('instagramLabel', 'Instagram'),
      url: instagramUrl,
    },
    {
      key: 'facebook',
      label: t('facebookLabel', 'Facebook'),
      url: facebookUrl,
    },
    {
      key: 'youtube',
      label: t('youtubeLabel', 'YouTube'),
      url: youtubeUrl,
    },
    {
      key: 'tiktok',
      label: t('tiktokLabel', 'TikTok'),
      url: tiktokUrl,
    },
    {
      key: 'whatsapp',
      label: t('whatsappLabel', 'WhatsApp'),
      url: whatsappUrl,
    },
  ].filter((item) => item.url);
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ''
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ''
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;
  const locationQuery = hasValidCoordinates
    ? `${latitude},${longitude}`
    : [address, city, country].filter(Boolean).join(', ');

  const mapSrc =
    `https://maps.google.com/maps?q=${encodeURIComponent(
      locationQuery,
    )}&z=15&output=embed`;

  const openGroundBooking = (ground = null) => {
    if (!canBook) {
      return;
    }

    openBooking?.(ground);
  };

  useScrollAnimation(!previewMode);

  useEffect(() => {
    if (previewMode) return undefined;

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    if (prefersReducedMotion) {
      lenisRef.current = null;
      return undefined;
    }

    const lenis = new Lenis({
      lerp: 0.12,
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 0.9,
      touchMultiplier: 1,
    });

    lenisRef.current = lenis;
    let animationFrameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      animationFrameId = window.requestAnimationFrame(raf);
    };

    animationFrameId = window.requestAnimationFrame(raf);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      lenis.destroy();

      if (lenisRef.current === lenis) {
        lenisRef.current = null;
      }
    };
  }, [previewMode]);

  useEffect(() => {
  if (previewMode) return undefined;

  const onScroll = () => {
    setScrolled(window.scrollY > 60);
    setParallaxY(window.scrollY * 0.32);
  };

  onScroll();

  window.addEventListener('scroll', onScroll, {
    passive: true,
  });

  return () => {
    window.removeEventListener('scroll', onScroll);
  };
}, [previewMode]);

  // Stats count-up trigger
  useEffect(() => {
    if (!statsRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsActive(true); },
      { threshold: 0.3 }
    );
    obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    if (previewMode) return;

    const element = document.getElementById(id);

    if (!element) {
      if (id === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      return;
    }

    const offset = id === 'top' ? 0 : -88;
    const lenis = lenisRef.current;

    if (lenis) {
      lenis.scrollTo(element, {
        offset,
        lerp: 0.12,
      });
      return;
    }

    const top =
      element.getBoundingClientRect().top +
      window.scrollY +
      offset;

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div
      className={
        previewMode
          ? 'ba-theme ba-theme-preview'
          : 'ba-theme'
      }
      id="top"
      data-background-style={backgroundStyle}
      style={boxArenaThemeStyle}
    >

      {/* ── NAVBAR ─────────────────────────────────────────────────────── */}
      <header className={`ba-nav ${scrolled ? 'scrolled' : ''} ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <div className="ba-nav-inner">
          {/* Logo */}
          <a href="#top" className="ba-logo" onClick={() => setMobileMenuOpen(false)}>
            <div className="ba-logo-wrap">
              {resolvedLogoImage ? (
                <img
                  src={resolvedLogoImage}
                  alt={t(
                    "logoAlt",
                    `${venueName} logo`,
                    `${venueName} logo`,
                  )}
                  className="ba-logo-img"
                />
              ) : (
                <span
                  aria-label={`${venueName} logo`}
                  style={{
                    fontSize: "1rem",
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {venueName}
                </span>
              )}
              <span className="ba-logo-sub">{city}</span>
            </div>
          </a>
          {/* Desktop nav */}
          <nav className="ba-nav-links">
            {navLinks.map((link) => (
              <button
                key={link.target}
                className="ba-nav-link"
                onClick={() => scrollTo(link.target)}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Mobile navigation toggle */}
          <button
            type="button"
            className={`ba-mobile-menu-toggle ${mobileMenuOpen ? 'is-open' : ''}`}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="boxarena-mobile-navigation"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* CTA */}
          <button
            className="ba-btn-primary ba-btn-sm"
            onClick={() => {
              setMobileMenuOpen(false);
              openGroundBooking();
            }}
          >
            {resolvedCtaLabel} ⚡
          </button>
        </div>
        <div
          id="boxarena-mobile-navigation"
          className={`ba-mobile-menu ${mobileMenuOpen ? 'is-open' : ''}`}
        >
          <nav className="ba-mobile-menu-nav" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <button
                key={link.target}
                type="button"
                className="ba-mobile-menu-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  scrollTo(link.target);
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "hero") && (
      <section className="ba-hero">
        <div
          className="ba-hero-bg"
          style={{ transform: `translateY(${parallaxY}px)` }}
        >
          <img
            src={heroImg}
            alt={venueName}
            className="ba-hero-img"
          />
          <div className="ba-hero-overlay"></div>
        </div>
        <div className="ba-hero-content">
          <div className="ba-hero-badge" data-animate="fade-down">
            <span className="ba-pulse"></span>
            {resolvedHeroBadge}
          </div>
          <h1 className="ba-hero-title" data-animate="fade-up">
            {t("heroTitleLineOne", "PLAY.")}<br />
            <span className="ba-text-green">
              {t("heroTitleHighlight", "EAT.")}
            </span><br />
            {t("heroTitleLineThree", "REPEAT.")}
          </h1>
          <p className="ba-hero-sub" data-animate="fade-up" style={{ '--delay': '0.15s' }}>
            {resolvedSubheadline}
          </p>
          <div className="ba-hero-actions" data-animate="fade-up" style={{ '--delay': '0.3s' }}>
            <button className="ba-btn-primary ba-btn-lg" onClick={() => openGroundBooking()}>
              {resolvedCtaLabel}
            </button>
            {isWebsiteSectionVisible(website, "grounds") && (
              <button className="ba-btn-outline ba-btn-lg" onClick={() => scrollTo('grounds')}>
                {t("heroExploreLabel", "Explore Grounds →")}
              </button>
            )}
          </div>
          {/* Floating badges */}
          <div className="ba-hero-badges" data-animate="fade-up" style={{ '--delay': '0.45s' }}>
            <div className="ba-badge-pill">{t("heroBadgeOne", "🏟️ 3 Premium Grounds")}</div>
            <div className="ba-badge-pill">{t("heroBadgeTwo", "⚡ Instant Booking")}</div>
            <div className="ba-badge-pill">{t("heroBadgeThree", "🌙 Open Every Day")}</div>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="ba-scroll-indicator">
          <div className="ba-scroll-mouse"></div>
          <span>{t("scrollLabel", "Scroll")}</span>
        </div>
      </section>
      )}

      {/* ── SPORTS / GROUNDS ───────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "grounds") && (
      <section id="grounds" className="ba-section ba-bg-light">
        <div className="ba-container">
          <div className="ba-section-header" data-animate="fade-up">
            <p className="ba-eyebrow">{t("groundsEyebrow", "// WHAT WE OFFER")}</p>
            <h2 className="ba-section-title">{t("groundsTitle", "CHOOSE YOUR SPORT.")}</h2>
            <p className="ba-section-sub">
              {t(
                "groundsDescription",
                "Three elite grounds. One venue. Book in under 30 seconds.",
              )}
            </p>
          </div>
          <div className="ba-sports-grid">
            {resolvedSports.map((s, i) => (
              <div
                key={s.id}
                className="ba-sport-card"
                data-animate="fade-up"
                style={{ '--delay': `${i * 0.12}s` }}
              >
                <div className="ba-sport-img-wrap">
                  <img src={s.img} alt={s.name} className="ba-sport-img" />
                  <div className="ba-sport-overlay"></div>
                  <span className="ba-sport-emoji">{s.emoji}</span>
                </div>
                <div className="ba-sport-info">
                  <h3 className="ba-sport-name">{s.name}</h3>
                  <p className="ba-sport-tagline">{s.tagline}</p>
                  <button className="ba-sport-book" onClick={() => openGroundBooking(s.ground)}>
                    {t("groundBookLabel", "Book Now →", legacyCopy.groundBookLabel)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── STATS ──────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "stats") && (
      <section ref={statsRef} className="ba-section ba-bg-dark ba-stats-section">
        <div className="ba-container">
          <div className="ba-stats-grid">
            {resolvedStats.map((s) => (
              <StatItem
                key={s.label}
                n={s.n}
                suffix={s.suffix}
                rawValue={s.rawValue}
                label={s.label}
                isActive={statsActive}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── ABOUT ──────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "about") && (
      <section id="about" className="ba-section ba-bg-light">
        <div className="ba-container">
          <div className="ba-about-grid">
            <div className="ba-about-img-wrap" data-animate="fade-right">
              <img
                src={resolvedHeroImage}
                alt={venueName}
                className="ba-about-img"
              />
              <div className="ba-about-img-badge">
                <span className="ba-pulse ba-pulse-green"></span>
                {t("aboutOpenBadge", "Open 7 Days a Week")}
              </div>
            </div>
            <div className="ba-about-content" data-animate="fade-left">
              <p className="ba-eyebrow">{t("aboutEyebrow", "// ABOUT BOXARENA", legacyCopy.aboutEyebrow)}</p>
              <h2 className="ba-section-title">
                {t("aboutTitleLineOne", "WHERE CHAMPIONS")}<br />
                {t("aboutTitleLineTwo", "ARE MADE.")}
              </h2>
              <p className="ba-about-desc">
                {t(
                  "aboutParagraphOne",
                  "BoxArena is Kathmandu's most complete multi-sport facility. Built for players who demand the best — from FIFA-grade artificial turf to tournament-ready pickleball courts, we provide elite playing conditions so you can focus on your game.",
                )}
              </p>
              <p className="ba-about-desc" style={{ marginTop: '16px' }}>
                {t(
                  "aboutParagraphTwo",
                  "Located at Tangal-02, Kathmandu, we're open every day of the week with floodlights for night sessions, free parking, and equipment rental on-site.",
                )}
              </p>
              <div className="ba-about-pills">
                {resolvedAboutFeatures.map((feature) => (
                  <span key={feature}>{feature}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── BOOKING ────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "booking") && (
      <section id="booking" className="ba-section ba-bg-dark">
        <div className="ba-container">
          <div className="ba-booking-grid">
            {/* Left: cred */}
            <div className="ba-booking-cred" data-animate="fade-right">
              <p className="ba-eyebrow ba-eyebrow-green">{t("bookingEyebrow", "// GET IN TOUCH", legacyCopy.bookingEyebrow)}</p>
              <h2 className="ba-section-title ba-title-light">
                {t("bookingTitleLineOne", "LET'S")}<br />
                {t("bookingTitleLineTwo", "TALK.")}
              </h2>
              <p className="ba-cred-desc">
                {t(
                  "bookingDescription",
                  "Have a question or want to discuss a corporate event? Send us a message and our team will get back to you shortly.",
                  legacyCopy.bookingDescription,
                )}
              </p>
              <div className="ba-cred-features">
                {resolvedBookingFeatures.map((feature) => (
                  <div className="ba-cred-feat" key={feature}>
                    <span className="ba-feat-dot"></span>
                    {feature}
                  </div>
                ))}
              </div>
              <blockquote className="ba-quote">
                <p>
                  {t(
                    "bookingQuote",
                    '"The BoxArena team was incredibly helpful in organizing our annual corporate futsal tournament. Highly recommended!"',
                  )}
                </p>
                <cite>{t("bookingQuoteAuthor", "— Arun KC., Event Organizer")}</cite>
              </blockquote>
            </div>
            {/* Right: form */}
            <div className="ba-booking-form-wrap" data-animate="fade-left">
              <form className="ba-form" onSubmit={handleFormSubmit}>
                <PublicContactHoneypot value={formData.company} onChange={handleFormChange} />
                <div className="ba-form-row">
                  <div className="ba-form-group">
                    <label>{t("formNameLabel", "Full Name *")}</label>
                    <input name="name" type="text" autoComplete="name" maxLength={100} placeholder={t("formNamePlaceholder", "Your name")} value={formData.name} onChange={handleFormChange} required />
                  </div>
                  <div className="ba-form-group">
                    <label>{t("formPhoneLabel", "Phone *")}</label>
                    <PublicContactPhoneField
                      value={formData.phone}
                      countryCode={formData.phoneCountryCode}
                      onChange={handleFormChange}
                      placeholder={t("formPhonePlaceholder", "+977 XXXXX XXXXX")}
                      required
                    />
                  </div>
                </div>
                <div className="ba-form-group">
                  <label>{t("formEmailLabel", "Email *")}</label>
                  <input name="email" type="email" autoComplete="email" maxLength={254} placeholder={t("formEmailPlaceholder", "you@email.com")} value={formData.email} onChange={handleFormChange} required />
                </div>
                <div className="ba-form-group">
                  <label>{t("formMessageLabel", "Message *")}</label>
                  <textarea name="message" rows="4" maxLength={1200} placeholder={t("formMessagePlaceholder", "How can we help you?")} value={formData.message} onChange={handleFormChange} required></textarea>
                </div>
                <button type="submit" className="ba-btn-primary ba-btn-lg ba-btn-full" disabled={contactSubmitting}>
                  {contactSubmitting ? "Sending…" : t("formSubmitLabel", "⚡ Send Message")}
                </button>
                <PublicContactEnquiryStatus submitting={contactSubmitting} feedback={contactFeedback} />
                <p className="ba-form-note">{t("formNote", "We aim to respond within 24 hours.")}</p>
              </form>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── TESTIMONIALS ───────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "testimonials") && (
      <section className="ba-section ba-bg-light">
        <div className="ba-container">
          <div className="ba-section-header" data-animate="fade-up">
            <p className="ba-eyebrow">{t("testimonialsEyebrow", "// PLAYER REVIEWS", legacyCopy.testimonialsEyebrow)}</p>
            <h2 className="ba-section-title">{t("testimonialsTitle", "LOVED BY PLAYERS.", legacyCopy.testimonialsTitle)}</h2>
          </div>
          <div className="ba-testimonials-grid">
            {resolvedTestimonials.map((t, i) => (
              <div
                key={t.name}
                className="ba-testimonial-card"
                data-animate="fade-up"
                style={{ '--delay': `${i * 0.12}s` }}
              >
                <StarRating rating={t.rating} />
                <p className="ba-testimonial-text">"{t.text}"</p>
                <div className="ba-testimonial-author">
                  <div className="ba-author-avatar">{t.name[0]}</div>
                  <div>
                    <p className="ba-author-name">{t.name}</p>
                    <p className="ba-author-sport">{t.sport}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "faqs") && (
      <section className="ba-section ba-bg-dark ba-faq-section">
        <div className="ba-container ba-container-sm">
          <div className="ba-section-header" data-animate="fade-up">
            <p className="ba-eyebrow ba-eyebrow-green">
              {t("faqEyebrow", "// FAQ", legacyCopy.faqEyebrow)}
            </p>
            <h2 className="ba-section-title ba-title-light">{t("faqTitle", "GOT QUESTIONS?", legacyCopy.faqTitle)}</h2>
          </div>
          <div className="ba-faq-list" data-animate="fade-up">
            {resolvedFaqs.map((f) => (
              <FaqItem
                key={f.q}
                q={f.q}
                a={f.a}
              />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── APP CTA ────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "app") && (
      <section className="ba-section ba-bg-green-dark ba-app-section">
        <div className="ba-container">
          <div className="ba-app-card" data-animate="scale-in">
            <div className="ba-app-content">
              <p className="ba-eyebrow ba-eyebrow-dark">
                {t("appEyebrow", "// GET THE APP")}
              </p>
              <h2 className="ba-app-title">
                {t("appTitleLineOne", "BOOK ANYWHERE.")}<br />
                {t("appTitleLineTwo", "PLAY EVERYWHERE.")}
              </h2>
              <p className="ba-app-desc">
                {t(
                  "appDescription",
                  "Download the ONGROUND app to discover and book BoxArena slots instantly — plus hundreds of sports venues across Nepal.",
                )}
              </p>
              <div
                id="app-downloads"
                className="ba-app-buttons"
              >
                <a
                  href={googlePlayUrl || "#app-downloads"}
                  className="ba-store-btn"
                  aria-disabled={!googlePlayUrl}
                  target={googlePlayUrl ? "_blank" : undefined}
                  rel={googlePlayUrl ? "noreferrer" : undefined}
                  onClick={
                    googlePlayUrl
                      ? undefined
                      : (event) => event.preventDefault()
                  }
                >
                  <OngroundStoreIcon
                    platform="googlePlay"
                    className="ba-store-icon"
                  />
                  <div>
                    <p className="ba-store-sub">{t("googlePlaySmallLabel", "GET IT ON")}</p>
                    <p className="ba-store-main">{t("googlePlayMainLabel", "Google Play")}</p>
                  </div>
                </a>

                <a
                  href={appStoreUrl || "#app-downloads"}
                  className="ba-store-btn"
                  aria-disabled={!appStoreUrl}
                  target={appStoreUrl ? "_blank" : undefined}
                  rel={appStoreUrl ? "noreferrer" : undefined}
                  onClick={
                    appStoreUrl
                      ? undefined
                      : (event) => event.preventDefault()
                  }
                >
                  <OngroundStoreIcon
                    platform="appStore"
                    className="ba-store-icon"
                    monochromeColor="#ffffff"
                  />
                  <div>
                    <p className="ba-store-sub">{t("appStoreSmallLabel", "DOWNLOAD ON THE")}</p>
                    <p className="ba-store-main">{t("appStoreMainLabel", "App Store")}</p>
                  </div>
                </a>
              </div>
            </div>
            <div className="ba-app-img-side">
              <img
                src={resolvedAppImage}
                alt={t("appImageAlt", "BoxArena turf")}
                className="ba-app-side-img"
              />
              <div className="ba-app-side-overlay"></div>
              <div className="ba-app-side-badge">
                <p className="ba-side-badge-num">{t("appBadgeValue", "500+")}</p>
                <p className="ba-side-badge-label">{t("appBadgeLabel", "Active players monthly")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── CONTACT ────────────────────────────────────────────────────── */}
      {isWebsiteSectionVisible(website, "contact") && (
      <section id="contact" className="ba-section ba-bg-light">
        <div className="ba-container">
          <div className="ba-section-header" data-animate="fade-up">
            <p className="ba-eyebrow">{t("contactEyebrow", "// GET IN TOUCH", legacyCopy.contactEyebrow)}</p>
            <h2 className="ba-section-title">{t("contactTitle", "FIND US.", legacyCopy.contactTitle)}</h2>
          </div>
          <div className="ba-contact-grid">
            <div className="ba-contact-info" data-animate="fade-right">
              <div className="ba-contact-item">
                <div className="ba-contact-icon">📍</div>
                <div>
                  <p className="ba-contact-label">{t("addressLabel", "Address")}</p>
                  <p className="ba-contact-val">{address}</p>
                </div>
              </div>
              <div className="ba-contact-item">
                <div className="ba-contact-icon">📞</div>
                <div>
                  <p className="ba-contact-label">{t("phoneLabel", "Phone / WhatsApp")}</p>
                  <p className="ba-contact-val">{phone}</p>
                </div>
              </div>
              {instagramUrl && (
                <div className="ba-contact-item">
                  <div className="ba-contact-icon">📸</div>
                  <div>
                    <p className="ba-contact-label">{t("instagramLabel", "Instagram")}</p>
                    <a
                      className="ba-contact-val"
                      href={instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {instagramHandle || t("instagramLabel", "Instagram")}
                    </a>
                  </div>
                </div>
              )}
              <div className="ba-contact-item">
                <div className="ba-contact-icon">⏰</div>
                <div>
                  <p className="ba-contact-label">{t("hoursLabel", "Hours")}</p>
                  <p className="ba-contact-val">{t("hoursPrefix", "Every day ·", legacyCopy.hoursPrefix)} {hours}</p>
                </div>
              </div>
            </div>
            {/* Map */}
            <div className="ba-map-wrap" data-animate="fade-left" style={{ position: 'relative' }}>
              <iframe
                title={t("mapTitle", "BoxArena Location")}
                className="ba-map"
                loading="lazy"
                src={mapSrc}
                allowFullScreen
              ></iframe>
              <OpenMapsLink
                url={venue?.googleMapsUrl}
                latitude={venue?.location?.latitude}
                longitude={venue?.location?.longitude}
                venueName={venueName}
                label={t('openMapsLabel', 'Open in Maps')}
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER ─────────────────────────────────────────────────────── */}
      <footer className="ba-footer">
        <div className="ba-container">
          <div className="ba-footer-grid">
            <div>
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="ba-logo" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="ba-logo-wrap">
                  {resolvedLogoImage ? (
                    <img
                      src={resolvedLogoImage}
                      alt={t(
                        "logoAlt",
                        `${venueName} logo`,
                        `${venueName} logo`,
                      )}
                      className="ba-logo-img"
                    />
                  ) : (
                    <span
                      aria-label={`${venueName} logo`}
                      style={{
                        fontSize: "1rem",
                        fontWeight: 900,
                        lineHeight: 1,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}
                    >
                      {venueName}
                    </span>
                  )}
                  <span className="ba-logo-sub">{city}</span>
                </div>
              </button>
              <p className="ba-footer-tagline">{t("footerTagline", "Play. Eat. Repeat.")}</p>
              <p className="ba-footer-address">{address}</p>
            </div>
            <div>
              <p className="ba-footer-col-title">{t("footerNavigationTitle", "Navigation")}</p>
              <ul className="ba-footer-links">
                {navLinks.map((link) => (
                  <li key={link.target}>
                    <button onClick={() => scrollTo(link.target)}>
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="ba-footer-col-title">{t("footerContactTitle", "Contact", legacyCopy.footerContactLabel)}</p>
              <ul className="ba-footer-links">
                <li>{phone}</li>
                {instagramUrl && (
                  <li>
                    <a
                      href={instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {instagramHandle || t("instagramLabel", "Instagram")}
                    </a>
                  </li>
                )}
                <li>{t("footerOpenPrefix", "Open")} {hours}</li>
              </ul>
            </div>
            {footerSocialItems.length > 0 && (
              <div>
                <p className="ba-footer-col-title">
                  {t("footerSocialTitle", "Socials")}
                </p>

                <ul className="ba-footer-links ba-footer-social-links">
                  {footerSocialItems.map((item) => (
                    <li key={item.key}>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <SocialIcon type={item.key} />
                        <span>{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="ba-footer-bottom">
            <p>
              © {new Date().getFullYear()} {venueName}, {city}
              {t("footerCopyrightSuffix", ". All rights reserved.", legacyCopy.footerCopyrightSuffix)}
            </p>
            <a
              className="ba-footer-powered"
              href={ongroundUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={t(
                "footerPoweredByAriaLabel",
                "Visit Onground website",
              )}
            >
              <span>
                {t(
                  "footerPoweredByLabel",
                  "Powered by",
                  legacyCopy.footerPoweredByText,
                )}
              </span>
              <img
                src={resolvedPoweredLogo}
                alt={t("footerPoweredLogoAlt", "ONGROUND")}
              />
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default BoxArenaTheme;
